import React, { useState, FormEvent, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { authService } from '../api';
import { isPasswordRequiredError } from '../utils/passwordRequired';
import AuthHeader from '../components/Header/AuthHeader';
import Button from '../components/Button';
import '@fortawesome/fontawesome-free/css/all.min.css';

type ResultStatus = 'success' | 'error' | null;

const SifreDegistir: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [oldPassword, setOldPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [formError, setFormError] = useState<string>('');
  const [resultStatus, setResultStatus] = useState<ResultStatus>(null);
  const [resultMessage, setResultMessage] = useState<string>('');

  useEffect(() => {
    if (user === undefined) return;
    if (user === null) {
      navigate('/giris', { state: { from: '/sifre-degistir' } });
      return;
    }
    if (user.hasPassword === false) {
      navigate('/sifre-olustur?from=/sifre-degistir', { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setFormError('');

    const trimmedOld = oldPassword.trim();
    if (!trimmedOld) {
      setFormError('Mevcut şifrenizi girin.');
      return;
    }

    if (!newPassword.trim()) {
      setFormError('Yeni şifre girin.');
      return;
    }

    if (newPassword !== newPasswordConfirmation) {
      setFormError('Yeni şifre ve tekrar alanları eşleşmiyor.');
      return;
    }

    setLoading(true);
    try {
      const result = await authService.changePassword({
        oldPassword: trimmedOld,
        newPassword,
        newPasswordConfirmation,
      });
      if (result.isSuccess) {
        setResultMessage(result.message ?? 'Şifreniz başarıyla güncellendi.');
        setResultStatus('success');
      } else {
        setResultMessage(result.message ?? 'Şifre güncellenemedi. Lütfen bilgilerinizi kontrol edin.');
        setResultStatus('error');
      }
    } catch (err) {
      if (isPasswordRequiredError(err)) {
        navigate('/sifre-olustur?from=/sifre-degistir');
        return;
      }
      const message = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string })?.message
        : null;
      setResultMessage(message ?? (err instanceof Error ? err.message : 'Bir hata oluştu. Lütfen tekrar deneyin.'));
      setResultStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const handlePopupClose = (): void => {
    setResultStatus(null);
    setResultMessage('');
    navigate('/profil');
  };

  if (user === null || user === undefined) {
    return null;
  }

  return (
    <>
      <AuthHeader />
      <div className="sb-page-bg min-vh-100 d-flex align-items-center">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-5">
              <div className="card shadow-sm">
                <div className="card-body p-4">
                  <h4 className="card-title text-center mb-4">
                    Şifre Değiştir
                  </h4>
                  <p className="text-muted text-center mb-4">
                    Mevcut şifrenizi ve yeni şifrenizi (iki kez) girin.
                  </p>

                  {formError && (
                    <div className="alert alert-danger mb-3" role="alert">
                      {formError}
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <label className="form-label" htmlFor="oldPassword">
                        Mevcut Şifre
                      </label>
                      <input
                        className="form-control"
                        id="oldPassword"
                        type="password"
                        required
                        minLength={1}
                        autoComplete="current-password"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label" htmlFor="newPassword">
                        Yeni Şifre
                      </label>
                      <input
                        className="form-control"
                        id="newPassword"
                        type="password"
                        required
                        minLength={1}
                        autoComplete="new-password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                    </div>
                    <div className="mb-4">
                      <label className="form-label" htmlFor="newPasswordConfirmation">
                        Yeni Şifre (Tekrar)
                      </label>
                      <input
                        className="form-control"
                        id="newPasswordConfirmation"
                        type="password"
                        required
                        minLength={1}
                        autoComplete="new-password"
                        value={newPasswordConfirmation}
                        onChange={(e) => setNewPasswordConfirmation(e.target.value)}
                      />
                    </div>
                    <Button
                      className="btn btn-primary w-100 mb-3"
                      type="submit"
                      loading={loading}
                    >
                      Şifremi Güncelle
                    </Button>
                    <div className="text-center">
                      <Link className="text-decoration-none" to="/profil">
                        <i className="fas fa-arrow-left me-1" />
                        Profil&apos;e dön
                      </Link>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Result popup – success or error; button redirects to profile */}
      {resultStatus && (
        <>
          <div
            className="modal fade show"
            style={{ display: 'block' }}
            tabIndex={-1}
            aria-modal="true"
            role="dialog"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content text-center p-4">
                <div className="modal-body py-4">
                  {resultStatus === 'success' ? (
                    <>
                      <div className="text-success mb-3">
                        <i className="fas fa-check-circle" style={{ fontSize: '4rem' }} />
                      </div>
                      <h5 className="mb-2">Şifre Güncellendi</h5>
                      <p className="text-muted mb-0">
                        {resultMessage}
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="text-danger mb-3">
                        <i className="fas fa-times-circle" style={{ fontSize: '4rem' }} />
                      </div>
                      <h5 className="mb-2">Şifre Güncellenemedi</h5>
                      <p className="text-muted mb-0">
                        {resultMessage}
                      </p>
                    </>
                  )}
                </div>
                <div className="modal-footer border-0 justify-content-center pb-4">
                  <Button
                    type="button"
                    className="btn btn-primary"
                    onClick={handlePopupClose}
                  >
                    Tamam
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </>
  );
};

export default SifreDegistir;
